import { Delete } from "@mui/icons-material";
import React from "react";
import styled from "styled-components";
import { Box, Button, Modal, Paper, Stack } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../../components/providers/auth-context";
import request from "../../../utils/request";
import { useTeams } from "../teams-provider";
import useTeamAction from "../hooks/use-team-action";
import { ToastContext } from "../../../components/providers/toast-provider";

const TeamDeletion = styled(Button)`
    font-size: 12px;
    font-weight: 300;
    text-transform: none;
    padding: 0;
    color: ${({ theme }) => theme.palette.text.secondary};

    &:hover {
        background-color: ${({ theme }) => theme.palette.background.default};
    }
`;

const ModalContainer = styled(Paper)`
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
`;
const Title = styled.h1`
    font-size: 18px;
    font-weight: 500;
    padding: 0;
    margin: 0;
`;

const SubTitle = styled.h2`
    font-size: 12px;
    font-weight: 400;
    padding: 0;
    margin: 0;
    color: ${({ theme }) => theme.palette.warning.main};
`;

const TeamDelete = ({ team }) => {
    const [open, setOpen] = React.useState(false);
    const { setMessage, setSeverity, setIsToastTriggered } = React.useContext(ToastContext);
    const { user } = useAuth();
    const teamsApi = useTeams();
    const navigate = useNavigate();

    if (!team) return null;

    const deleteAction = useTeamAction(
        async () => {
            await request(`/api/teams/${team.id}`, { method: "DELETE" });
            setMessage(`${team.name} successfully deleted.`);
            setSeverity("success");
            setIsToastTriggered(true);
            teamsApi.refetch();
            setTimeout(() => {
                navigate("/teams");
            }, 500);
        },
        () => {
            setMessage(`You can not perform this action on demo mode`);
            setSeverity("warning");
            setIsToastTriggered(true);
        }
    );

    const ownerId = team.owner && team.owner.id;

    const handleSubmit = async (e) => {
        e.stopPropagation();
        e.preventDefault();
        setIsToastTriggered(false);
        try {
            deleteAction();
        } catch (error) {
            setMessage(`There was an error deleting ${team.name}. Are you the owner?`);
            setSeverity("error");
            setIsToastTriggered(true);
        }
    };

    return (
        <>
            <TeamDeletion
                startIcon={<Delete />}
                disabled={ownerId !== user.id}
                onClick={() => setOpen(true)}
            >
                Delete Team
            </TeamDeletion>
            <Modal
                open={open}
                onClose={() => {
                    setIsToastTriggered(false);
                    setOpen(false);
                }}
                aria-labelledby="team-delete-modal-title"
                aria-describedby="team-delete-modal-description"
            >
                <ModalContainer>
                    <Box p={3}>
                        <form onSubmit={handleSubmit}>
                            <Stack spacing={2} alignItems="flex-start">
                                <Title>Are you sure you want to delete team {team.name}</Title>
                                <SubTitle>This action is not reversible</SubTitle>
                                <Stack
                                    direction="row"
                                    justifyContent="space-between"
                                    style={{ width: "100%" }}
                                >
                                    <Button
                                        variant="contained"
                                        onClick={() => {
                                            setOpen(false);
                                        }}
                                    >
                                        Cancel
                                    </Button>
                                    <Button type="submit" color="error">
                                        Delete
                                    </Button>
                                </Stack>
                            </Stack>
                        </form>
                    </Box>
                </ModalContainer>
            </Modal>
        </>
    );
};

export default TeamDelete;
